import React from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuPlacement, MenuPosition } from 'react-select';
import './select.css';
import FormField from '../../common/form-field';
import { parseError } from '../../../services/utils';
import css from './read-input.module.css';
import AsyncSelect, {
  AsyncSelectType,
  ObservableOptions,
} from './select/async-select';
import SyncSelect, { SyncSelectType } from './select/sync-select';

export const emptySelectOption = { value: '', label: '' };

export type Option = {
  label: string;
  value?: string;
  options?: Option[];

  [key: string]: any;
};

type SelectType = {
  label?: string;
  options?: Option[];
  asyncOptions?: ObservableOptions;
  multi?: boolean;
  clearable?: boolean;
  cacheOn?: string[] | ((parentName: string) => string[]);
  creatable?: boolean;
  grouped?: boolean;
  inline?: boolean;
  column?: boolean;
  disabled?: boolean;
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
  horizontal?: boolean;
  className?: string;
  requiredShow?: boolean;
  isClearable?: boolean;
};

export default function Select(
  name: string,
  properties: SelectType,
  horizontal?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <SelectComponent
      name={name}
      multi={false}
      label={null}
      cacheOn={[]}
      grouped={false}
      inline={false}
      horizontal={horizontal}
      {...rest}
      {...properties}
    />
  );
}

export function SelectComponent({
  name,
  options,
  label,
  multi,
  parentName,
  disabled,
  clearable,
  readonly,
  required,
  asyncOptions,
  creatable,
  cacheOn,
  grouped,
  inline,
  menuPlacement,
  menuPosition,
  horizontal,
  column,
  requiredShow = false,
  className,
  isClearable = false,
}: SelectType &
  FormComponentProps & {
    name: string;
    className?: string;
  }) {
  const { t } = useTranslation();
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const selectName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(selectName, errors);

  const selectedValue = watch(name);

  const isClear = isClearable
    ? selectedValue?.value
      ? true
      : false
    : clearable != null
    ? clearable
    : !required?.includes(selectName);

  if (readonly) {
    return (
      <FormField
        inline={inline}
        column={column}
        label={label ? t(label) : label === null ? null : t(name)}
        labelFor={selectName}
        horizontal={horizontal}
        className={className}
      >
        <Controller
          name={selectName}
          defaultValue={null}
          render={({ field: { value } }) => {
            const findValue = (
              value: string[] | string | any,
              option: Option
            ): boolean => {
              if (option.options) {
                return (
                  option.options.filter((o) => findValue(value, o)).length !== 0
                );
              } else if (Array.isArray(value)) {
                return value.includes(option.value);
              } else if (typeof value === 'object' && value !== null) {
                return option.value === value?.value;
              } else {
                return option.value === value;
              }
            };

            let foundValue = null;

            if (options?.length) {
              foundValue = options
                .filter((option) => findValue(value, option))
                .flatMap((option) => {
                  if (option.options) {
                    return option.options.filter((o) => findValue(value, o));
                  } else {
                    return [option];
                  }
                });
            }

            switch (true) {
              case Array.isArray(foundValue):
                value = foundValue?.map((item: any) => item.label);
                break;
              case Array.isArray(value):
                value = value.map((item: any) => item.label);
                break;
              case typeof value === 'object':
                value = value?.label;
                break;
            }

            return multi ? (
              <textarea
                readOnly={true}
                rows={value ? value.length : 1}
                className={css['readonly']}
                value={value ? value.join('\n') : ''}
                name={selectName}
              />
            ) : (
              <input
                readOnly={true}
                type="text"
                className={css['readonly']}
                value={value ?? ''}
                title={value ?? ''}
                name={selectName}
              />
            );
          }}
        />
      </FormField>
    );
  } else {
    return (
      <FormField
        inline={inline}
        column={column}
        label={label ? t(label) : label === null ? null : t(name)}
        labelFor={selectName}
        required={required?.includes(selectName) || requiredShow}
        error={error ? error.message : null}
        horizontal={horizontal}
        className={className}
      >
        <SelectController
          creatable={creatable}
          cacheOn={cacheOn}
          options={options}
          multi={multi}
          disabled={disabled}
          clearable={isClear}
          asyncOptions={asyncOptions}
          name={name}
          parentName={parentName}
          grouped={grouped}
          menuPlacement={menuPlacement}
          menuPosition={menuPosition}
        />
      </FormField>
    );
  }
}

export function SelectController({
  creatable,
  cacheOn,
  asyncOptions,
  name,
  parentName,
  disabled,
  clearable,
  options,
  multi,
  grouped,
  menuPlacement = 'bottom',
  menuPosition = 'absolute',
}: SyncSelectType & AsyncSelectType) {
  if (asyncOptions) {
    return (
      <AsyncSelect
        name={name}
        parentName={parentName}
        cacheOn={cacheOn}
        disabled={disabled}
        multi={multi}
        creatable={creatable}
        clearable={clearable}
        grouped={grouped}
        asyncOptions={asyncOptions}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
      />
    );
  } else {
    return (
      <SyncSelect
        name={name}
        parentName={parentName}
        disabled={disabled}
        options={options}
        multi={multi}
        clearable={clearable}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
      />
    );
  }
}
